import cx from 'classnames';
import {isEmpty, map, toUpper} from 'lodash';
import moment from 'moment';
import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {DotLineDivider} from 'components';
import {UserContext} from 'context';
import {WeekDay} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {SpecialDayWorkingHoursItem, WeekDayWorkingHours, WorkingHoursTimePoint} from 'types';
import {buildTranslationKey} from 'utils/transform';

import {DayStatus} from '../DayStatus';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.WORKING_HOURS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    day?: WeekDay;
    item: SpecialDayWorkingHoursItem | WeekDayWorkingHours;
    showExtraInfo: boolean;
}

export const WorkingHoursItemTitle: React.FC<Props> = ({item, day, showExtraInfo}) => {
    const {t} = useTranslation();

    const {settings: {common: {dateFormat, timeFormat}}} = useContext(UserContext);
    const isSpecialDay = useCallback((_item): _item is SpecialDayWorkingHoursItem => !!_item.day, []);
    const isDayOff = useMemo(() => isEmpty(item.intervals), [item]);

    const dayName = useMemo(() => {
        if (isSpecialDay(item)) {
            const date = moment().set('date', item.day).set('month', item.month).set('year', item.year);
            return date.format(dateFormat);
        } else {
            return t(T_KEYS_COMMON.WEEK_DAYS[toUpper(day)]);
        }
    }, [day, item, isSpecialDay, dateFormat, t]);

    const repeatText = useMemo(() => {
        if (isSpecialDay(item)) {
            const tKey = buildTranslationKey(item.repeat);
            if (T_KEYS.SPECIAL_HOURS.REPEAT_OPTIONS[tKey]) {
                return t(T_KEYS.SPECIAL_HOURS.REPEAT_OPTIONS[tKey]);
            }
        }
    }, [isSpecialDay, item, t]);

    const getFormattedTime = useCallback(({hours, minutes}: WorkingHoursTimePoint) => {
        const date = moment().set('hours', hours).set('minutes', minutes);
        return date.format(timeFormat);
    }, [timeFormat]);

    const renderFormattedIntervals = useCallback(() => {
        const textIntervals = map(item.intervals, (interval) =>
            interval?.from && interval?.to ? `${getFormattedTime(interval.from)}-${getFormattedTime(interval.to)}` : '');
        return map(textIntervals, (textInterval, index) => {
            const isLast = index + 1 === textIntervals.length;
            return (
                <div key={textInterval} className='tw-flex tw-text-base tw-items-center tw-mr-1'>
                    <div className={cx({'tw-mr-4': isLast})}>{textInterval}</div>
                    {!isLast && <DotLineDivider className='tw-ml-4 tw-bg-dark -sm:tw-hidden' />}
                </div>
            );
        });
    }, [item, getFormattedTime]);

    const renderName = useCallback(() => {
        if (isSpecialDay(item)) {
            return item.name ? (<div className='tw-font-bold tw-text-base'>{`(${item.name})`}</div>) : null;
        } else {
            return null;
        }
    }, [item, isSpecialDay]);

    return (
        <div className='tw-flex tw-flex-col sm:tw-flex-row tw-mr-4'>
            <div className='tw-flex tw-items-center'>
                <DayStatus intervals={item.intervals} isSpecialDay={isSpecialDay(item)} className='tw-mr-4 tw-flex-shrink-0' />
                <div className={cx('tw-mr-4 tw-min-w-24', {
                    'tw-text-default-dimmed': isDayOff && !isSpecialDay(item)
                })}
                >
                    <div className='tw-font-bold'>{dayName}</div>
                    {showExtraInfo && repeatText &&
                        <div>{`${t(T_KEYS.SPECIAL_HOURS.REPEAT_LABEL)} ${repeatText}`}</div>}
                </div>
            </div>
            {showExtraInfo &&
                <div className='tw-flex tw-items-center -sm:tw-mt-4'>
                    {isEmpty(item.intervals) &&
                        <div className='tw-flex tw-text-base sm:tw-items-center tw-mr-4'>
                            {t(T_KEYS.DAY_STATUS.NON_WORKING_DAY)}
                        </div>}
                    <div className='tw-flex -sm:tw-flex-col sm:tw-flex-wrap sm:tw-items-center'>
                        {renderFormattedIntervals()}
                        {renderName()}
                    </div>
                </div>}
        </div>
    );
};